import type { JobSource } from '@/papermill/stages';

import styles from './JobGlyph.module.css';

export interface JobGlyphProps {
  source: JobSource;
  /** Adds a star badge: one of ours, run for free. */
  sample?: boolean;
  /** Animates a live session's dot while it is still in flight. */
  active?: boolean;
  className?: string;
}

const LABELS: Record<JobSource, string> = {
  document: 'Document',
  photo: 'Photo',
  audio: 'Recording',
  live: 'Live transcription',
};

function Paths({ source }: { source: JobSource }) {
  switch (source) {
    case 'photo':
      return (
        <>
          <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.8l1.4-2h4.6l1.4 2h1.8A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5z" />
          <circle cx="12" cy="12.5" r="3.4" />
        </>
      );
    case 'audio':
      return (
        <>
          <rect x="9" y="3" width="6" height="11" rx="3" />
          <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7" />
        </>
      );
    case 'live':
      return (
        <>
          <circle cx="12" cy="12" r="2.2" className={styles.dot} />
          <path d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 8.2a5.4 5.4 0 0 1 0 7.6M5.3 5.3a9.5 9.5 0 0 0 0 13.4M18.7 5.3a9.5 9.5 0 0 1 0 13.4" />
        </>
      );
    default:
      return (
        <>
          <path d="M6.5 3h7L18 7.5v11A2.5 2.5 0 0 1 15.5 21h-9A2.5 2.5 0 0 1 4 18.5v-13A2.5 2.5 0 0 1 6.5 3z" />
          <path d="M13.5 3v4.5H18M7.5 12h7M7.5 15.5h5" />
        </>
      );
  }
}

/** What a job was made from, at a glance: a file, a photo, a recording or a live call. */
export function JobGlyph({ source, sample = false, active = false, className }: JobGlyphProps) {
  return (
    <span
      className={[styles.glyph, styles[source], active ? styles.active : '', className]
        .filter(Boolean)
        .join(' ')}
      role="img"
      aria-label={sample ? `Sample ${LABELS[source].toLowerCase()}` : LABELS[source]}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <Paths source={source} />
      </svg>
      {sample ? (
        <span className={styles.badge} aria-hidden="true">
          ★
        </span>
      ) : null}
    </span>
  );
}
